import { CatalogMoldSize } from "@/pages/caster/models/catalogs/CatalogMoldSize";
import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import GetAppIcon from "@mui/icons-material/GetApp";
import {
	Button,
	CircularProgress,
	Grid2 as Grid,
	IconButton,
	Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Dispatch, SetStateAction, useState } from "react";
import {
	DeliveryTicket,
	ProductBatch,
} from "../../../../../models/DeliveryTicket";
import { upsertProductBatch } from "../../../../../repositories/ProductionRepository";
import { useCastersContext } from "../../../../CastersContext";
import { calculateWeightPerPiece } from "../../../utils/calculateWeightPerPiece";
import { useDeliveryTicketStyles } from "../styles/deliveryTicketStyles";
import { LengthInput } from "./components/LengthInput";
import { PiecesInput } from "./components/PiecesInput";
import { SectionInput } from "./components/SectionInput";
import { WeightInput } from "./components/WeightInput";
import { WeightPerPieceInput } from "./components/WieghtPerPieceInput";
interface StrandFieldsProps {
	deliveryTicket: DeliveryTicket | null;
	moldSizes: CatalogMoldSize[] | null;
	handleProductBatchChange: (index: number, key: string, value: string) => void;
	errors: Map<string, string>;
	setErrors: Dispatch<SetStateAction<Map<string, string>>>;
	refreshProductBatches: () => void;
	isLoading: boolean;
	ladleClose?: Date | null;
}

const moment = getMomentTz();
export const ProductBatchSection = ({
	errors,
	setErrors,
	deliveryTicket,
	moldSizes,
	handleProductBatchChange,
	refreshProductBatches,
	isLoading,
	ladleClose,
}: StrandFieldsProps) => {
	const classes = useDeliveryTicketStyles();
	const titleOfColumns = ["Length", "Pieces", "Section", "Weight", "Weight/Pc"];
	const [error, setError] = useState("");
	const { state, actions } = useCastersContext();

	const isProcessClosed = () => {
		// Check ladleClose prop first, fallback to context if not provided
		if (ladleClose !== undefined) {
			return ladleClose != null;
		}
		return state.processData?.LadleClose != null;
	};

	// Helper function to get the most recent ProductBatch update info
	const getLastUpdateInfo = () => {
		if (
			!deliveryTicket?.productBatches ||
			deliveryTicket.productBatches.length === 0
		) {
			return null;
		}

		// Find the ProductBatch with the most recent timestamp
		const mostRecentBatch = deliveryTicket.productBatches.reduce(
			(latest, current) => {
				const latestTime = latest.Timestamp;
				const currentTime = current.Timestamp;
				return currentTime > latestTime ? current : latest;
			},
		);
		// Format the timestamp to MM/DD/YYYY HH:mm:ss in local time
		const formattedDateTime = moment(mostRecentBatch.Timestamp).format(
			"MM/DD/YYYY - HH:mm:ss",
		);

		return {
			dateTime: formattedDateTime,
			user: mostRecentBatch.ReportedBy || "System",
		};
	};

	const handleAddField = (
		e: React.MouseEvent | null,
		initialWeight: string | null = null,
	) => {
		if (e) e.preventDefault();
		if (deliveryTicket && deliveryTicket.productBatches.length < 8) {
			const newPBId = -Math.floor(Math.random() * 1000000) - 1;

			const newBatch: any = {
				ProductBatchId: newPBId, // Random negative integer
				Length: "0",
				Pieces: 1,
				SectionName: "",
				SectionId: 0,
				Weight: initialWeight || 0,
				WeightPerPiece: "",
			};
			const updatedBatches = [...deliveryTicket.productBatches, newBatch];
			handleProductBatchChange(
				-1,
				"updateAllBatches",
				JSON.stringify(updatedBatches),
			);
		}
	};

	const handleDeleteBatch = async (batch: ProductBatch) => {
		if (deliveryTicket && state.processId) {
			const batchToDelete = deliveryTicket.productBatches.find(
				(pb) => pb.ProductBatchId === batch.ProductBatchId,
			);
			// PBatch exists in db?
			if (batchToDelete?.ProductBatchId && batchToDelete?.ProductBatchId > 0) {
				const resp = await upsertProductBatch({
					productBatchId: batchToDelete?.ProductBatchId,
					sectionId: batchToDelete?.SectionId,
					processId: state.processId as number,
					weight: 0,
					pieces: 0,
					length: batchToDelete?.Length,
					isManualSaved: true,
				});

				if (!resp.ok) {
					setError(resp.message);
					return;
				}
			}
			// Front-ent list
			const updatedBatches = deliveryTicket.productBatches.filter(
				(pb) => pb.ProductBatchId !== batch.ProductBatchId,
			);
			handleProductBatchChange(
				-1,
				"updateAllBatches",
				JSON.stringify(updatedBatches),
			);
			setErrors((prevErrors) => {
				const newErrors = new Map(prevErrors);
				for (const key of newErrors.keys()) {
					if (key.endsWith(`-${batch.ProductBatchId}`)) {
						newErrors.delete(key);
					}
				}
				return newErrors;
			});
		}
	};
	const handleLengthChange = (
		batchIndex: number,
		feet: number,
		inches: number,
		ProductBatchId: number,
	) => {
		const totalInches = feet * 12 + inches;
		handleProductBatchChange(batchIndex, "Length", totalInches.toString());

		// Clear any existing errors for this field
		setErrors((prev) => {
			const newErrors = new Map(prev);
			newErrors.delete(`Length-${ProductBatchId}`);
			return newErrors;
		});
	};
	const handleSectionChange = (
		value: string,
		batchIndex: number,
		ProductBatchId: number,
	) => {
		handleProductBatchChange(batchIndex, "SectionId", value);
		// Clear any existing errors for this field
		setErrors((prev) => {
			const newErrors = new Map(prev);
			newErrors.delete(`Section-${ProductBatchId}`);
			return newErrors;
		});
	};

	const handleWeightChange = (
		value: string,
		batchIndex: number,
		ProductBatchId: number,
	) => {
		handleProductBatchChange(batchIndex, "Weight", value);

		// Clear any existing errors for this field
		setErrors((prev) => {
			const newErrors = new Map(prev);
			newErrors.delete(`Weight-${ProductBatchId}`);
			return newErrors;
		});
	};

	const getWeightFromMoldSize = () => {
		return (
			moldSizes
				?.find((ms) => ms.MoldSizeId === deliveryTicket?.heatData.MoldSizeId)
				?.LbsPerFoot.toString() || ""
		);
	};

	return (
		<>
			<Grid container spacing={1}>
				<Grid container size={{ xs: 12 }} spacing={2} alignItems="center">
					<Grid size={{ xs: 1 }}></Grid>
					{titleOfColumns.map((field, index) => {
						const width = field === "Pieces" ? 1 : field === "Section" ? 3 : 2;
						return (
							<Grid size={{ xs: width }} key={field}>
								<Typography
									align="center"
									style={{
										fontWeight: "bold",
										marginRight:
											index === titleOfColumns.length - 1 ? "24px" : "0",
									}}
								>
									{field}
								</Typography>
							</Grid>
						);
					})}
					<Grid size={{ xs: 1 }}>
						<Tooltip
							title={
								isProcessClosed()
									? "Get from strands"
									: "Get from strands will be enabled when the ladle is closed"
							}
							arrow
							placement="top"
						>
							<span>
								<Button
									startIcon={isLoading && <CircularProgress size="1rem" />}
									fullWidth
									variant="outlined"
									color="primary"
									disabled={!isProcessClosed() || isLoading}
									onClick={() => {
										if (isProcessClosed()) {
											refreshProductBatches();
										}
									}}
								>
									<GetAppIcon />
								</Button>
							</span>
						</Tooltip>
					</Grid>
				</Grid>

				{deliveryTicket?.productBatches.map((batch, batchIndex) => (
					<Grid
						container
						size={{ xs: 12 }}
						spacing={2}
						alignItems="center"
						key={batchIndex}
					>
						<Grid size={{ xs: 1 }}></Grid>
						<Grid size={{ xs: 2 }}>
							<LengthInput
								feet={Math.floor(Number(batch.Length) / 12) || 0}
								inches={Number(batch.Length) % 12 || 0}
								onChange={(feet, inches) =>
									handleLengthChange(
										batchIndex,
										feet,
										inches,
										batch.ProductBatchId,
									)
								}
								error={errors.has(`Length-${batch.ProductBatchId}`)}
								helperText={errors.get(`Length-${batch.ProductBatchId}`)}
								className={classes.input}
							/>
						</Grid>
						<Grid size={{ xs: 1 }}>
							<PiecesInput
								value={batch.Pieces}
								onChange={(value) =>
									handleProductBatchChange(batchIndex, "Pieces", value)
								}
								error={errors.has(`Pieces-${batch.ProductBatchId}`)}
								helperText={errors.get(`Pieces-${batch.ProductBatchId}`)}
								className={classes.input}
							/>
						</Grid>
						<Grid size={{ xs: 3 }}>
							<SectionInput
								value={batch.SectionId}
								onChange={(value) =>
									handleSectionChange(value, batchIndex, batch.ProductBatchId)
								}
								className={`${classes.input}`}
								error={errors.has(`Section-${batch.ProductBatchId}`)}
								helperText={errors.get(`Section-${batch.ProductBatchId}`)}
							/>
						</Grid>
						<Grid size={{ xs: 2 }}>
							<WeightInput
								value={batch.Weight}
								onChange={(value) =>
									handleWeightChange(value, batchIndex, batch.ProductBatchId)
								}
								error={errors.has(`Weight-${batch.ProductBatchId}`)}
								helperText={errors.get(`Weight-${batch.ProductBatchId}`)}
								className={classes.input}
							/>
						</Grid>
						<Grid size={{ xs: 2 }}>
							<WeightPerPieceInput
								value={
									batch.WeightPerPiece ||
									(batch.Weight && batch.Pieces
										? calculateWeightPerPiece(batch.Weight, batch.Pieces)
										: "")
								}
								error={errors.has(`WeightPerPiece-${batch.ProductBatchId}`)}
								helperText={errors.get(
									`WeightPerPiece-${batch.ProductBatchId}`,
								)}
								className={classes.disabledInput + " " + classes.input}
							/>
						</Grid>
						<Grid size={{ xs: 1 }} container justifyContent="center">
							{(batchIndex > 0 ||
								(batch.ProductBatchId < 0 &&
									!deliveryTicket?.productBatches.some(
										(b) => b.ProductBatchId > 0,
									))) && (
								<IconButton
									onClick={() => handleDeleteBatch(batch)}
									size="small"
									color="error"
									title="Delete"
								>
									<DeleteIcon style={{ cursor: "pointer" }} />
								</IconButton>
							)}
						</Grid>
					</Grid>
				))}

				<Grid size={{ xs: 1 }}></Grid>
				{deliveryTicket && deliveryTicket.productBatches.length < 8 && (
					<Grid size={{ xs: 2 }}>
						<IconButton
							color="primary"
							className={`${classes.addButton}`}
							size="medium"
							onClick={(e) => handleAddField(e)}
							title="Add new line"
						>
							<AddCircleOutlineOutlinedIcon
								fontSize="medium"
								style={{ cursor: "pointer" }}
							/>
						</IconButton>
					</Grid>
				)}

				{(() => {
					const lastUpdateInfo = getLastUpdateInfo();
					if (lastUpdateInfo) {
						return (
							<Grid size={{ xs: 12 }} container justifyContent="flex-end">
								<Typography
									variant="caption"
									style={{
										color: "#6c757d",
										fontSize: "0.75rem",
										fontStyle: "italic",
										marginRight: "20px",
									}}
								>
									Last updated: {lastUpdateInfo.dateTime} by{" "}
									{lastUpdateInfo.user}
								</Typography>
							</Grid>
						);
					}
					return null;
				})()}
			</Grid>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
